// KPI-ASSIGN: picking metrics by name instead of typing keys (Role defaults). The choices are the
// company's metrics and the DE catalog's (a catalog metric not defined yet is named "DE
// catalog"); a picked metric shows as a chip with its name, the key in its tooltip.
import { Button, Input } from "@companyos/ui";
import { PlusIcon, SearchIcon, XIcon } from "lucide-react";
import { useState } from "react";
import type { MetricDef, MetricTemplateSet } from "../../api/types";
import { CADENCE_OPTIONS, labelOf, UNIT_OPTIONS } from "../structure/labels";
import { sourceText } from "./kpi-assign-dialog";

export type MetricChoice = { key: string; name: string; detail: string };

export function metricChoices(
  defs: readonly MetricDef[],
  templates: readonly MetricTemplateSet[],
): MetricChoice[] {
  const out = new Map<string, MetricChoice>();
  const detail = (m: Pick<MetricDef, "source" | "unit" | "cadence">) =>
    `${sourceText(m)} · ${labelOf(UNIT_OPTIONS, m.unit)} · ${
      m.cadence ? labelOf(CADENCE_OPTIONS, m.cadence) : "no cadence"
    }`;
  for (const m of defs) out.set(m.key, { key: m.key, name: m.name, detail: detail(m) });
  for (const set of templates)
    for (const m of set.metrics)
      if (!out.has(m.key))
        out.set(m.key, { key: m.key, name: m.name, detail: `DE catalog · ${detail(m)}` });
  return [...out.values()].sort((a, b) => a.name.localeCompare(b.name));
}

export function MetricMultiPicker({
  id,
  value,
  choices,
  onChange,
  disabled,
}: {
  id: string;
  value: readonly string[];
  choices: readonly MetricChoice[];
  onChange: (keys: string[]) => void;
  disabled?: boolean;
}) {
  const [find, setFind] = useState("");
  const byKey = new Map(choices.map((c) => [c.key, c]));
  const q = find.trim().toLowerCase();
  const open = choices
    .filter((c) => !value.includes(c.key))
    .filter((c) => !q || c.name.toLowerCase().includes(q) || c.key.includes(q))
    .slice(0, 8);
  return (
    <div className="flex flex-col gap-2" data-slot="metric-multi-picker">
      {value.length ? (
        <ul className="flex flex-wrap gap-1" aria-label="Picked metrics">
          {value.map((k) => (
            <li
              key={k}
              title={k}
              className="inline-flex items-center gap-1 rounded-md border border-border bg-card py-0.5 pr-0.5 pl-2 text-xs"
            >
              {byKey.get(k)?.name ?? <span className="font-mono">{k}</span>}
              <Button
                type="button"
                size="icon-xs"
                variant="ghost"
                aria-label={`Remove ${byKey.get(k)?.name ?? k}`}
                disabled={disabled}
                onClick={() => onChange(value.filter((x) => x !== k))}
              >
                <XIcon />
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="relative">
        <SearchIcon
          aria-hidden="true"
          className="pointer-events-none absolute top-2.5 left-2.5 size-4 text-muted-foreground"
        />
        <Input
          id={id}
          className="pl-8"
          placeholder="Find a metric to add…"
          value={find}
          disabled={disabled}
          onChange={(e) => setFind(e.target.value)}
        />
      </div>
      {open.length && !disabled ? (
        <ul className="flex flex-col rounded-lg border border-border" aria-label="Metrics to add">
          {open.map((c) => (
            <li key={c.key} className="border-b border-border last:border-b-0">
              <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
                aria-label={`Add ${c.name}`}
                onClick={() => {
                  onChange([...value, c.key]);
                  setFind("");
                }}
              >
                <PlusIcon aria-hidden="true" className="size-3.5 text-muted-foreground" />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm">{c.name}</span>
                  <span className="truncate text-xs text-muted-foreground">{c.detail}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
